import type { ChapterEnum, WorkOrderOtherPriceData, WorkOrderPriceData } from 'gql/graphql';
import { WorkOrderPriceStatusEnum, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { IUseDeletableWorkOrderItemsResponse } from '../hooks/useDeletableWorkOrderItems';
import { DeletableWorkOrderItemsStatus } from '../hooks/useDeletableWorkOrderItems';
import {
    checkActKC2OtherHaveNoZeroPrices,
    checkCanEditDistribution,
} from '../WorkOrderOthersTable/checkActKC2OtherHaveNoZeroPrices';
import { checkIsDisabledByWorkOrderStatus } from '../WorkOrderOthersTable/checkIsDisabledByWorkOrderStatus';

const hasActPlanVolumeMessage = 'Имеется фактическое выполнение. Для редактирования очистите значения в ячейках Акта ';

export const useWorkOrderRowMenuItems = <R extends WorkOrderPriceData | (WorkOrderOtherPriceData & { type: string })>(
    row: R,
    hasActs: boolean,
    chapter: ChapterEnum,
    workOrderStatus?: WorkOrderStatusEnum,
    deletableItems?: IUseDeletableWorkOrderItemsResponse,
) => {
    const navigate = useNavigate();

    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            orderStatus: workOrderStatus,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderStatus,
        ],
    );

    const isStatusDraftOrNew = useMemo(
        () =>
            row.status &&
            [WorkOrderPriceStatusEnum.Draft, WorkOrderPriceStatusEnum.NewRecord].includes(
                row.status as WorkOrderPriceStatusEnum,
            ),
        [row.status],
    );

    const isDuplicate = useMemo(() => 'hasDuplicates' in row && row.hasDuplicates, [row]);

    const isEditDisabledByEmptyPlanVolume = useMemo(
        () => 'hasEmptyActPlanVolume' in row && !row.hasEmptyActPlanVolume,
        [row],
    );

    const isEditDisabled = useMemo(
        () =>
            (workOrderStatus === WorkOrderStatusEnum.InProgress && 'isActKC2Price' in row && !row.isActKC2Price) ||
            [WorkOrderPriceStatusEnum.ContractCondition, WorkOrderPriceStatusEnum.AdditionalCondition].includes(
                row.status as WorkOrderPriceStatusEnum,
            ),
        [row, workOrderStatus],
    );

    const matchItemMenu = useMemo(
        () => [
            {
                label: 'Сопоставить с Прейскурантом МТР',
                action: () => {
                    if ('priceListId' in row) {
                        navigate(`matchMtr/${row?.id}/${row?.priceListId}`, { replace: true, state: row });
                    }
                },
            },
        ],
        [navigate, row],
    );

    const editNoteItemMenu = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          label: 'Редактировать примечание',
                          action: () => {
                              navigate(`editRationale/${row.id}`, { replace: true, state: row });
                          },
                      },
                  ]
                : [],
        [workOrderStatus, row, navigate],
    );

    const removeItemMenu = useMemo(
        () => [
            {
                label: 'Удалить',
                action: () => {
                    navigate(`removeRow/${row.id}`, { replace: true });
                },
                disabled:
                    deletableItems?.status === DeletableWorkOrderItemsStatus.noDeletable ||
                    (deletableItems?.status === DeletableWorkOrderItemsStatus.partlyDeletable &&
                        !deletableItems?.deletableIds?.includes(row.id)),
            },
        ],
        [deletableItems, row.id, navigate],
    );

    const fillDataMTRItemMenu = useMemo(
        () => [
            {
                label: 'Редактировать',
                action: () => {
                    if ('priceListId' in row) {
                        navigate(`editMtr/${row.priceListId}/${row.id}`, { replace: true });
                    }
                },
                disabled: isEditDisabledByEmptyPlanVolume || isEditDisabled,
                haveTooltip: isEditDisabledByEmptyPlanVolume,
                tooltipName: 'hasEmptyActPlanVolume',
                tooltipMsg: hasActPlanVolumeMessage,
            },
        ],
        [isEditDisabledByEmptyPlanVolume, isEditDisabled, row, navigate],
    );

    const mtrMenu = useMemo(
        () =>
            isStatusDraftOrNew ? [...fillDataMTRItemMenu, ...removeItemMenu] : [...editNoteItemMenu, ...removeItemMenu],
        [isStatusDraftOrNew, fillDataMTRItemMenu, removeItemMenu, editNoteItemMenu],
    );

    const mtrMenuWithMatch = useMemo(
        () => (row.status === WorkOrderPriceStatusEnum.Draft && isDuplicate ? [...matchItemMenu, ...mtrMenu] : mtrMenu),
        [row.status, isDuplicate, matchItemMenu, mtrMenu],
    );

    const addCoefficientMenuItem = useMemo(() => {
        const label = 'Добавить коэффициент';

        return {
            label,
            action: () => {
                navigate(`addCoefficient/${row.id}`, { replace: true, state: row });

                if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                    sendGpnAnalyticsEvent({
                        componentId: 'rim_NZ_add coef button_pressed',
                        ...analyticsData,
                    });
                } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                    sendGpnAnalyticsEvent({
                        componentId: 'rim_act KC_add coef button_pressed',
                        ...analyticsData,
                    });
                }
            },
        };
    }, [analyticsData, navigate, row, sendGpnAnalyticsEvent, workOrderStatus]);

    const uerMenu = useMemo(
        () => [
            ...(isStatusDraftOrNew
                ? [
                      {
                          label: 'Редактировать',
                          action: () => {
                              if ('priceListId' in row) {
                                  navigate(`editUer/${row.priceListId}/${row.id}`, {
                                      replace: true,
                                  });

                                  sendGpnAnalyticsEvent({
                                      componentId: 'rim_edit price button_selected',
                                      ...analyticsData,
                                  });
                              }
                          },
                          disabled: isEditDisabledByEmptyPlanVolume || isEditDisabled,
                          haveTooltip: isEditDisabledByEmptyPlanVolume,
                          tooltipName: 'hasEmptyActPlanVolume',
                          tooltipMsg: hasActPlanVolumeMessage,
                      },
                  ]
                : []),
            {
                label: 'Добавить МТР',
                action: () => {
                    navigate(`addMtr/${row.id}`, { replace: true, state: { chapter } });

                    if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_NZ_add MTR button_pressed',
                            ...analyticsData,
                        });
                    } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                        sendGpnAnalyticsEvent({
                            componentId: 'rim_act KC_add MTR button_pressed',
                            ...analyticsData,
                        });
                    }
                },
            },
            ...(workOrderStatus === WorkOrderStatusEnum.OnFormation || hasActs
                ? [addCoefficientMenuItem]
                : [{ ...addCoefficientMenuItem, disabled: true }]),
            ...editNoteItemMenu,
            ...removeItemMenu,
        ],
        [
            addCoefficientMenuItem,
            analyticsData,
            chapter,
            editNoteItemMenu,
            hasActs,
            isEditDisabled,
            isEditDisabledByEmptyPlanVolume,
            isStatusDraftOrNew,
            navigate,
            removeItemMenu,
            row,
            sendGpnAnalyticsEvent,
            workOrderStatus,
        ],
    );

    const disabledByWorkOrderStatus = checkIsDisabledByWorkOrderStatus(workOrderStatus, row?.isActKC2Price);

    const othersMenu = useMemo(
        () => [
            ...(checkCanEditDistribution(row as WorkOrderOtherPriceData, workOrderStatus)
                ? [
                      {
                          label: 'Изменить распределение',
                          action: () => {
                              navigate(`editDist/${row.id}`);
                          },
                      },
                  ]
                : []),
            {
                label: 'Просмотреть',
                action: () => {
                    navigate(`viewOther/${(row as unknown as WorkOrderOtherPriceData)?.contractOtherId}`);
                },
            },
            ...(!row.isActKC2Price ? [] : [...editNoteItemMenu]),
            ...(disabledByWorkOrderStatus ||
            checkActKC2OtherHaveNoZeroPrices(row as any) ||
            workOrderStatus === WorkOrderStatusEnum.Completed
                ? []
                : [
                      {
                          label: 'Удалить',
                          action: () => {
                              navigate(`deleteOther/${row.id}`);
                          },
                      },
                  ]),
        ],
        [disabledByWorkOrderStatus, editNoteItemMenu, navigate, row, workOrderStatus],
    );

    const coefficientMenu = useMemo(
        () => [
            ...editNoteItemMenu,
            {
                label: 'Редактировать',
                action: () => {
                    if ('priceListId' in row) {
                        navigate(`editCoeff/${row?.priceListId}`, { state: row });
                    }
                },
                disabled:
                    (row.status &&
                        ![WorkOrderPriceStatusEnum.Draft, WorkOrderPriceStatusEnum.NewRecord].includes(
                            row.status as WorkOrderPriceStatusEnum,
                        )) ||
                    isEditDisabledByEmptyPlanVolume ||
                    isEditDisabled,
                haveTooltip: isEditDisabledByEmptyPlanVolume,
                tooltipName: 'hasEmptyActPlanVolume',
                tooltipMsg: hasActPlanVolumeMessage,
            },
            ...removeItemMenu,
        ],
        [editNoteItemMenu, isEditDisabled, isEditDisabledByEmptyPlanVolume, navigate, removeItemMenu, row],
    );

    const menuByType: Record<string, any> = useMemo(
        () => ({
            [WorkOrderPriceTypeEnum.Uer]: uerMenu,
            [WorkOrderPriceTypeEnum.Coefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.ConstraintCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.TechPartCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.Mtr]: mtrMenuWithMatch,
            [WorkOrderPriceTypeEnum.MtrContractorEquipment]: mtrMenuWithMatch,
            [WorkOrderPriceTypeEnum.MtrContractorMaterial]: mtrMenuWithMatch,
            [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: mtrMenuWithMatch,
            [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: mtrMenuWithMatch,
            OTHERS: othersMenu,
            OTHERS_NEW: othersMenu,
        }),
        [uerMenu, coefficientMenu, othersMenu, mtrMenuWithMatch],
    );

    return menuByType[row.type];
};
